/**
 * `/caps` — the non-DTF (caps) production worklist. Caps are produced by FSP but off the gang sheet
 * (heat-press / patch), so they never appear in the DTF order queue / batch build; this page is where
 * the floor sees and completes them. Marking a cap produced moves it off the worklist (OPEN → PRODUCED)
 * and writes NO blank movement. It's reversible: a just-produced cap sits in an "Undo" panel for the
 * rest of the session. Gated on a dashboard sign-in (a shop-floor operation, like the press station).
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, HardHat, Inbox, Loader2, RefreshCw, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageContainer } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { type CapLine, capKey, groupCapsByOrder } from './caps-lib';

const PAGE_LIMIT = 100;

function garmentLabel(l: CapLine): string {
  return l.variantTitle ? `${l.title} — ${l.variantTitle}` : l.title;
}

export function CapsPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canProduce = state.status === 'signed-in';

  // Cursor-paginated; drain all pages so the whole caps worklist + the count are accurate.
  const query = useInfiniteQuery({
    queryKey: ['caps', 'worklist'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/caps/worklist', {
        params: { query: { limit: PAGE_LIMIT, cursor: pageParam } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the caps worklist.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Session "just produced" list — the reversal affordance for a mis-click (the produced cap has left
  // the server worklist, so we keep the row here with an Undo until the page reloads).
  const [justProduced, setJustProduced] = useState<CapLine[]>([]);
  const [inFlight, setInFlight] = useState<Set<string>>(() => new Set());
  const justProducedKeys = useMemo(() => new Set(justProduced.map(capKey)), [justProduced]);

  const lines = (query.data?.pages.flatMap((p) => p.items) ?? []) as CapLine[];
  // Hide a just-produced line until the refetch drops it, so it never shows in both the worklist and
  // the Undo panel at once.
  const groups = useMemo(
    () => groupCapsByOrder(lines.filter((l) => !justProducedKeys.has(capKey(l)))),
    [lines, justProducedKeys],
  );
  const totalCaps = groups.reduce((n, g) => n + g.lines.length, 0);

  const produceMut = useMutation({
    mutationFn: async (vars: { line: CapLine; produced: boolean }) => {
      const { line, produced } = vars;
      const res = await client.POST('/caps/{storeId}/{orderId}/lines/{lineId}/produced', {
        params: { path: { storeId: line.storeId, orderId: line.orderId, lineId: line.lineId } },
        body: { produced },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onMutate: (vars) => {
      setInFlight((s) => new Set(s).add(capKey(vars.line)));
    },
    onSuccess: (_d, vars) => {
      setJustProduced((prev) =>
        vars.produced
          ? [vars.line, ...prev.filter((l) => capKey(l) !== capKey(vars.line))]
          : prev.filter((l) => capKey(l) !== capKey(vars.line)),
      );
    },
    onSettled: (_d, _e, vars) => {
      setInFlight((s) => {
        const n = new Set(s);
        n.delete(capKey(vars.line));
        return n;
      });
      qc.invalidateQueries({ queryKey: ['caps'] });
      qc.invalidateQueries({ queryKey: ['order', vars.line.storeId, vars.line.orderId] });
    },
  });

  const busy = (l: CapLine) => inFlight.has(capKey(l));

  return (
    <PageContainer>
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <HardHat className="size-6 text-muted-foreground" />
              Caps
              {totalCaps > 0 && <Badge variant="secondary">{totalCaps}</Badge>}
            </h1>
            <p className="text-sm text-muted-foreground">
              Non-DTF items (caps, patches) produced off the gang sheet. Mark each done as you
              heat-press it.{' '}
              <Link
                to="/catalog"
                search={{ filter: 'caps' }}
                className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
              >
                Map cap blanks →
              </Link>
            </p>
          </div>
          {/* A cap produced at another station leaves the server worklist, so this page's row silently
              goes stale with no way to reload short of a browser refresh — it had no refresh
              affordance at all. Mirrors the /press header button. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn('size-4', query.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </header>

        {!canProduce && (
          <Alert>
            <AlertTitle>Sign in to update caps</AlertTitle>
            <AlertDescription>
              You can view the worklist, but marking a cap produced needs a dashboard sign-in.
            </AlertDescription>
          </Alert>
        )}

        {query.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t load the caps worklist</AlertTitle>
            <AlertDescription>
              {query.error instanceof Error ? query.error.message : 'Please try again.'}
            </AlertDescription>
          </Alert>
        )}

        {produceMut.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t update the cap — nothing changed</AlertTitle>
            <AlertDescription>
              {produceMut.error instanceof Error ? produceMut.error.message : 'Please try again.'}
            </AlertDescription>
          </Alert>
        )}

        {query.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : totalCaps === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            <Inbox className="size-8" />
            <p className="text-sm">No caps waiting to be produced.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={`${g.storeId}#${g.orderId}`} className="rounded-lg border">
                <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
                  <span className="text-sm font-medium">{g.orderName}</span>
                  <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                    Cap — off-sheet
                  </Badge>
                </div>
                <ul className="divide-y">
                  {g.lines.map((l) => (
                    <li
                      key={capKey(l)}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{garmentLabel(l)}</p>
                        <p className="text-xs text-muted-foreground">Qty {l.unfulfilledQty}</p>
                      </div>
                      <Button
                        size="sm"
                        disabled={!canProduce || busy(l)}
                        onClick={() => produceMut.mutate({ line: l, produced: true })}
                      >
                        {busy(l) ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Check className="size-4" />
                        )}
                        Mark produced
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {justProduced.length > 0 && (
          <div className="rounded-lg border border-dashed">
            <div className="border-b px-4 py-2 text-sm font-medium text-muted-foreground">
              Just produced ({justProduced.length}) — undo a mis-click
            </div>
            <ul className="divide-y">
              {justProduced.map((l) => (
                <li key={capKey(l)} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm">
                      {l.orderName} · {garmentLabel(l)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canProduce || busy(l)}
                    onClick={() => produceMut.mutate({ line: l, produced: false })}
                  >
                    {busy(l) ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Undo2 className="size-4" />
                    )}
                    Undo
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
